import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import CssBaseline from '@mui/material/CssBaseline';
import { Paper, TableContainer } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import VirtualizedTable from '../../virtualized-table';
import { DealerTransactionsFetch } from './calls';
import { AppState } from '../../../store';
import { headCells,sortableAccessors} from '../transactions-table/head-cells';
import { clearDetailsMap, getTotalCountOfTransactions } from '../../../store/transactions/actions';
import { QueryParameterKeys } from '../../../globals/paramter';
import { Transaction } from '../../../store/transactions/types';
import RowDetails from '../row-details';
import TableHoverTooltip from '../../table-hover-tooltip';
import { expander } from '../../virtualized-table/components/expandable-row';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';
import { TransactionType } from '../../../entities/transactions-v1/query-types';
import { parametersAsObject, sleep, urlUpdate } from '../../../globals/utils';


interface Sorting {
  columnName: string;
  direction: 'desc' | 'asc';
}
interface Time {
  fetchStartTime: string;
  fetchEndTime: string;
}

type TransactionsFunctionReturn = {
  latestCursor: string;
  records: Transaction[];
  time: Time;
};

const transactionChunkSize = 200;

const loopTracker: any = {
  cursor: null,
  targetNumberOfRecords: transactionChunkSize,
  blockNewCalls: false,
  time: { fetchStartTime: '', fetchEndTime: '' },
};

const DealerTransactionsTable = ({
  tableContainer,
  id,
  resetTableString,
  columnOrder,
  columnVisibility,
  storageString,
  dealerCode,
}: {
  resetTableString: string;
  tableContainer: React.RefObject<HTMLElement>;
  id?: string;
  columnVisibility: string[];
  columnOrder: string[];
  storageString: string;
  dealerCode: string;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const columnWidths = window.localStorage.getItem(`${storageString}-column-widths`);
  const sortByString = window.localStorage.getItem(`${storageString}-sort-by`);
  const savedDefaultSortByString = window.localStorage.getItem(`${storageString}-default-sort-by`);
  const parameters: URLSearchParams = new URLSearchParams(history.location.search);
  const startTime = parameters.get(QueryParameterKeys.startTime) || undefined;
  const endTime = parameters.get(QueryParameterKeys.endTime) || undefined;
  const timeFrame = parameters.get(QueryParameterKeys.timeFrame) || '';
  const fileTypeIdFilters = parameters.get(QueryParameterKeys.fileTypeIdFilters) || undefined;
  const excludeFileTypeIdFilters = parameters.get(QueryParameterKeys.excludeFileTypeIdFilters) || undefined;
  const transactionTypes = parameters.get(QueryParameterKeys.category) || undefined;
  const vimsData = parameters.get(QueryParameterKeys.vimsData) || undefined;
  const viewFileSize = parameters.get(QueryParameterKeys.viewFileSize) || undefined;
  const userPreferences = useSelector((state: AppState) => state.userPreference);
  const availableRecordsLength = useSelector((state: AppState) => state.transactions.count);
  const { login } = useSelector((state: AppState) => state);
  const [loading, setLoading] = useState(false);
  const [previousCursors, setPreviousCursors] = useState<string[]>([]);
  const [rows, setRows] = useState<Transaction[]>([]);
  const [errorFlag,setErrorFlag]=useState<boolean>(false);
  const [sortBy, setSortBy] = useState<Sorting | null>(
    savedDefaultSortByString
      ? JSON.parse(savedDefaultSortByString)
      : sortByString
      ? JSON.parse(sortByString)
      : { columnName: 'gatewayReceiptTime', direction: 'desc' },
  );
  const [autoReset,setAutoReset]=useState<boolean>(false);
  const onColumnWidthsChange = (widths: { [key: string]: boolean }) => {
    window.localStorage.setItem(`${storageString}-column-widths`, JSON.stringify(widths));
  };
  
   const handleSortByChange = (sorting: Sorting) => {
    // setSortBy(sorting);
    // window.localStorage.setItem(`${storageString}-sort-by`, JSON.stringify(sorting));
    let sortingCreation: Sorting;
    if (sorting.columnName !== sortBy?.columnName) {
      sortingCreation = { columnName: sorting.columnName, direction: 'desc' };
      setSortBy(sortingCreation);
      window.localStorage.setItem(`${storageString}-sort-by`, JSON.stringify(sorting));
    }
    else {
      setSortBy(sorting);
      window.localStorage.setItem(`${storageString}-sort-by`, JSON.stringify(sorting));
    }
  };

  const getTransactions = new DealerTransactionsFetch(
    parameters,
    userPreferences,
    login,
    sortByString != null ? sortBy : { columnName: 'gatewayReceiptTime', direction: 'desc' },
    dealerCode,
  );
  const calculateFetchTime = () => {
    if (timeFrame && timeFrame !== 'custom') {
      urlUpdate(history, parametersAsObject(parameters), {
        startTime: `${moment().subtract(`${timeFrame}`, `hours`).startOf("minute").toISOString()}`,
        endTime: `${moment().endOf("minute").toISOString()}`,
        timeFrame,
      });
      return {
        fetchStartTime: `${moment().subtract(timeFrame, `hours`).startOf("minute").toISOString()}`,
        fetchEndTime: `${moment().endOf("minute").toISOString()}`,
      };
    }
    return {
      fetchStartTime: startTime || '',
      fetchEndTime: endTime || '',
    };
  };

  const fetchTransactions = async ({
    cursor,
    time,
    limit,
  }: {
    cursor: string;
    time: Time;
    limit?: number;
  }) => {
    setLoading(true);
    setPreviousCursors([...previousCursors, cursor]);
    const { fetchStartTime, fetchEndTime } = time;
    try {
      const transactionsResponse = await getTransactions.data(
        limit || transactionChunkSize,
        cursor,
        fetchStartTime,
        fetchEndTime,
      );
      if (errorFlag) {
        setErrorFlag(false);
      }
      return {
        latestCursor: transactionsResponse.cursor,
        records: transactionsResponse.data,
        time,
      };
    } catch (error: any) {
      setLoading(false);
      if (transactionTypes && transactionTypes.includes(TransactionType.dataFile))
        dispatch(
          enqueueSnackbarAction({
            message:
              'Currently sort by Status or Message Creation timestamp is not applicable when filtering Data File transactions',
            options: {
              variant: 'error',
            },
          }),
        );
      else if (error.message !== undefined && error.message && !error.message.includes('404')
        && !error.message.includes('Cannot read properties of undefined')) {
        setErrorFlag(true)
        dispatch(
          enqueueSnackbarAction({
            message: error.message,
            options: {
              variant: 'error',
            },
          }),
        );
      }
      return {
        latestCursor: null,
        records: [],
        time,
      };
    }
  };

  const setTransactions = ({ latestCursor, records, time }: TransactionsFunctionReturn) => {
    setRows(records);
    loopTracker.cursor = latestCursor;
    loopTracker.time = time;
    setLoading(false);
  };

  const fetchLoop = async (cursor: string, previousRows: Transaction[], time: Time) => {
    const transactionsResponse = await fetchTransactions({ cursor, time }).then(
      (response: TransactionsFunctionReturn) => {
        const { latestCursor, records } = response;
        if (autoReset) setAutoReset(false);
        const mergedRecords = [...previousRows, ...records];
        loopTracker.cursor = latestCursor;
        if (
          loopTracker.targetNumberOfRecords > mergedRecords.length &&
          latestCursor &&
          !previousCursors.includes(latestCursor)
        ) {
          fetchLoop(latestCursor, previousRows, time);
        } else {
          setTransactions({ latestCursor, records: mergedRecords, time });
        }
        return { latestCursor, records };
      },
    );
    return transactionsResponse;
  };

  const getAll = async () => {
    setRows([]);
    setPreviousCursors([]);
    dispatch(clearDetailsMap());
    setAutoReset(true);
    loopTracker.cursor = null;
    loopTracker.targetNumberOfRecords = transactionChunkSize * 2;
    loopTracker.blockNewCalls = false;
    setLoading(true);
    const time = calculateFetchTime();
    const { fetchStartTime, fetchEndTime } = time;
    const result = await sleep(fetchTransactions, { cursor: '', time, limit: 200 }, 10);
    const defaultTransactionTypes = transactionTypes || [TransactionType.message, TransactionType.command, TransactionType.dataFile].toString();
    dispatch(
      getTotalCountOfTransactions({
        startTime: fetchStartTime,
        endTime: fetchEndTime,
        filter: sortByString != null ? sortBy?.columnName : 'gatewayReceiptTime',
        deviceKey: dealerCode,
        fileTypeIdFilters,
        excludeFileTypeIdFilters,
        vimsData,
        transactionTypes: defaultTransactionTypes,
        timeFrame,
      }),
    );
    setTransactions(result);
    if (
      loopTracker.targetNumberOfRecords > result.records.length &&
      result.latestCursor &&
      !previousCursors.includes(result.latestCursor)
    )
      fetchLoop(result.latestCursor, result.records, time);
    return result;
  };

  const data = useMemo(() => rows || [], [rows]);
  const columns = useMemo(
    () => [
      expander,
      ...headCells.map((cell) => {
        const widths = columnWidths && JSON.parse(columnWidths);
        const width = widths && widths[cell.accessor];
        if (widths && width) return { ...cell, width };
        return cell;
      }),
    ],
    [columnWidths],
  );

  const handleSetLoadUpTo = (newLoadUpTo: number) => {
    if (
      newLoadUpTo > data.length &&
      rows &&
      loopTracker.cursor &&
      newLoadUpTo >= loopTracker.targetNumberOfRecords
    ) {
      loopTracker.targetNumberOfRecords = newLoadUpTo;
      fetchLoop(loopTracker.cursor, rows, loopTracker.time);
    }
  };

  useEffect(() => {
    if (timeFrame && (startTime && endTime)) {
      getAll();
    }
  },
    [
      dealerCode,
      transactionTypes,
      startTime,
      endTime,
      fileTypeIdFilters,
      excludeFileTypeIdFilters,
      sortBy,
      resetTableString,
      viewFileSize
    ]
  );

  useEffect(() => {
    if (savedDefaultSortByString) setSortBy(JSON.parse(savedDefaultSortByString));
  }, [savedDefaultSortByString]);

  useEffect(() => {
    if (sortByString === null) setSortBy({ columnName: 'gatewayReceiptTime', direction: 'desc' })
  }, [sortByString]);

  const itemCount = () => {
    const existingRecordsLength = data.length;
    if(errorFlag) return existingRecordsLength;
    if (availableRecordsLength > 10000) return existingRecordsLength + transactionChunkSize;
    if (sortBy?.columnName === 'messageCreationTime') return existingRecordsLength + transactionChunkSize; // this should be removed when the backend supports sorting by Created Time Stamp correctly
    if (existingRecordsLength + transactionChunkSize < availableRecordsLength)
      return existingRecordsLength + transactionChunkSize;
    return availableRecordsLength;
  };


  return (
    <Paper style={{ height: '100%', width: 'inherit', marginBottom: '24px', overflow: 'hidden' }}>
      {tableContainer && <TableHoverTooltip containerReference={tableContainer} />}
      <CssBaseline>
        <TableContainer id={id}>
          <VirtualizedTable
            tableContainer={tableContainer}
            initialSort={sortBy?.columnName || ''}
            initialSortDirection={sortBy?.direction || 'desc'}
            columns={columns}
            data={data}
            loading={loading}
            itemCount={itemCount()}
            chunkSize={transactionChunkSize}
            loadMoreItems={handleSetLoadUpTo}
            onSortChange={(sortByChange: Sorting) => handleSortByChange(sortByChange)}
            onColumnWidthsChange={onColumnWidthsChange}
            columnOrder={columnOrder}
            columnVisibility={columnVisibility}
            sortableAccessors={sortableAccessors}
            renderRowSubComponent={RowDetails}
            errorFlag={errorFlag}
            sortByString={sortByString}
            autoReset={autoReset}
          />
        </TableContainer>
      </CssBaseline>
    </Paper>
  );
};

export default DealerTransactionsTable;
